<template>
  <div class="sellers-container">
    <!-- Cabeçalho da página -->
    <div class="page-header">
      <h1 class="page-title">
        <i class="fa-solid fa-ticket"></i>
        Bilhetes da Região
      </h1>
      <p class="page-subtitle">Visualize todos os bilhetes realizados na sua região</p>
    </div>

    <!-- Filtros -->
    <div class="filters-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-filter"></i>
          Filtros de Consulta
        </h3>
      </div>
      
      <div class="filters-content">
        <div class="filters-grid">
          <!-- Data Início -->
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-calendar-days"></i>
              Data Início
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="filters.startDate" 
              placeholder="Data de início"
            />
          </div>

          <!-- Data Fim -->
          <div class="input-group">
            <label class="input-label required">
              <i class="fa-solid fa-calendar-days"></i>
              Data Fim
            </label>
            <input 
              class="form-input" 
              type="date" 
              v-model="filters.endDate" 
              placeholder="Data de fim"
            />
          </div>

          <!-- Vendedor -->
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-user"></i>
              Vendedor
            </label>
            <select class="form-input" v-model="filters.sellerId">
              <option value="">Todos os vendedores</option>
              <option 
                v-for="seller in filteredSellers" 
                :key="seller.id" 
                :value="seller.id"
              >
                {{ seller.name }}
              </option>
            </select>
          </div>

          <!-- Status -->
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-info-circle"></i>
              Status
            </label>
            <select class="form-input" v-model="filters.status">
              <option value="">Todos</option>
              <option value="active">Ativo</option>
              <option value="canceled">Cancelado</option>
              <option value="awarded">Premiado</option>
              <option v-if="!isDescargaRegion" value="redeemed">Resgatado</option>
              <option value="closed">Encerrado</option>
              <option value="expired">Expirado</option>
            </select>
          </div>

          <!-- Loteria -->
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-clover"></i>
              Loteria
            </label>
            <select class="form-input" v-model="filters.lotteryId" @change="onLotteryChange">
              <option value="">Todas as loterias</option>
              <option 
                v-for="lottery in lotteries" 
                :key="lottery.id" 
                :value="lottery.id"
              >
                {{ lottery.name }}
              </option>
            </select>
          </div>

          <!-- Hora do Sorteio -->
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-clock"></i>
              Hora do Sorteio
            </label>
            <select 
              class="form-input" 
              v-model="filters.lotteryDetailId"
              :disabled="!filters.lotteryId"
            >
              <option value="">Todos os horários</option>
              <option 
                v-for="detail in availableLotteryDetails" 
                :key="detail.id" 
                :value="detail.id"
              >
                {{ detail.description }}
              </option>
            </select>
          </div>

          <!-- Busca NSU / PIN -->
          <div class="input-group">
            <label class="input-label">
              <i class="fa-solid fa-magnifying-glass"></i>
              Buscar (NSU / PIN)
            </label>
            <input
              class="form-input"
              type="text"
              v-model="filters.search"
              placeholder="Digite o NSU ou PIN"
              autocomplete="off"
            />
          </div>
        </div>

        <div class="filters-actions">
          <button 
            type="button" 
            class="btn-primary" 
            @click="searchTickets()"
            :disabled="loading"
          >
            <i v-if="loading" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-search"></i>
            {{ loading ? 'Buscando...' : 'Pesquisar' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Mensagem informativa para usuários com permissões limitadas -->
    <div v-if="!isAdmin" class="permission-info">
      <div class="info-card">
        <i class="fa-solid fa-info-circle"></i>
        <div class="info-content">
          <span v-if="isBanca">
            <strong>Modo BANCA:</strong> Você pode visualizar bilhetes apenas dos vendedores de suas regiões.
          </span>
          <span v-else-if="isGerente">
            <strong>Modo GERENTE:</strong> Você pode visualizar bilhetes apenas dos vendedores de suas regiões.
          </span>
          <span v-else-if="isCobrador">
            <strong>Modo COBRADOR:</strong> Você pode visualizar bilhetes apenas dos vendedores vinculados a você.
          </span>
        </div>
      </div>
    </div>

    <!-- Resumo dos Bilhetes -->
    <div v-if="filteredTickets.length > 0" class="summary-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-chart-bar"></i>
          Resumo do Período
          <span v-if="selectedSellerName" class="seller-filter-info">
            - {{ selectedSellerName }}
          </span>
        </h3>
      </div>
      <div class="summary-content">
        <div class="summary-item">
          <div class="summary-icon">
            <i class="fa-solid fa-ticket"></i>
          </div>
          <div class="summary-info">
            <span class="summary-label">Bilhetes</span>
            <span class="summary-value">{{ activeTicketsForSummary.length }}</span>
          </div>
        </div>
        <div class="summary-item">
          <div class="summary-icon">
            <i class="fa-solid fa-dollar-sign"></i>
          </div>
          <div class="summary-info">
            <span class="summary-label">Total Apostas</span>
            <span class="summary-value">{{ setCurrencyString(totalBetsAmount) }}</span>
          </div>
        </div>
        <div class="summary-item">
          <div class="summary-icon">
            <i class="fa-solid fa-percentage"></i>
          </div>
          <div class="summary-info">
            <span class="summary-label">Comissão</span>
            <span class="summary-value">{{ setCurrencyString(totalCommission) }}</span>
          </div>
        </div>
        <div class="summary-item">
          <div class="summary-icon">
            <i class="fa-solid fa-trophy"></i>
          </div>
          <div class="summary-info">
            <span class="summary-label">Prêmios</span>
            <span class="summary-value">{{ setCurrencyString(totalPrizes) }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Loading -->
    <div v-show="loading" class="loading-container">
      <div class="loading-spinner">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <span>Carregando bilhetes...</span>
      </div>
    </div>

    <!-- Tabela de Bilhetes -->
    <div v-show="!loading && paginatedTickets.length > 0" class="results-card">
      <div class="card-header">
        <h3 class="card-title">
          <i class="fa-solid fa-table"></i>
          Lista de Bilhetes
          <span class="table-count">({{ filteredTickets.length }} bilhete(s) encontrado(s))</span>
        </h3>
        
        <!-- Controles de Paginação Superior -->
        <div v-if="totalPages > 1" class="pagination-info">
          <span>Página {{ currentPage + 1 }} de {{ totalPages }}</span>
          <div class="pagination-controls">
            <button 
              class="btn-pagination" 
              @click="changePage(currentPage - 1)"
              :disabled="currentPage === 0"
            >
              <i class="fa-solid fa-chevron-left"></i>
            </button>
            <button 
              class="btn-pagination" 
              @click="changePage(currentPage + 1)"
              :disabled="currentPage >= totalPages - 1"
            >
              <i class="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>
      
      <div class="table-container">
        <table class="modern-table">
          <thead>
            <tr>
              <th>NSU</th>
              <th>Data/Hora Aposta</th>
              <th>Valor</th>
              <th>Status</th>
              <th>Premiação</th>
              <th>Comissão</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="ticket in paginatedTickets" 
              :key="ticket.betId"
              class="data-row"
            >
              <td>
                <div class="nsu-info">
                  <span class="nsu-code">{{ ticket.nsu }}</span>
                  <span
                    v-if="(ticket.packageDrawCount || 1) > 1"
                    class="package-badge"
                    :title="`Bingo Semanal — ${ticket.packageDrawCount} extrações`"
                  >
                    {{ ticket.packageDrawCount }}x
                  </span>
                  <span class="qr-code" v-if="ticket.qrCode">
                    <i class="fa-solid fa-qrcode"></i>
                  </span>
                </div>
              </td>
              
              <td>
                <div class="datetime-info">
                  <span class="date">{{ formatDate(ticket.dateTimePersisted) }}</span>
                  <span class="time">{{ formatTime(ticket.dateTimePersisted) }}</span>
                </div>
              </td>
              
              <td>
                <span class="currency-value">{{ setCurrencyString(ticket.amount) }}</span>
              </td>
              
              <td>
                <span 
                  class="status-badge" 
                  :class="getStatusClass(ticket)"
                >
                  <i :class="getStatusIcon(ticket)"></i>
                  {{ getStatusText(ticket) }}
                </span>
              </td>
              
              <td>
                <span 
                  class="currency-value" 
                  :class="{ 'currency-positive': parseFloat(ticket.totalAwarded || 0) > 0 }"
                >
                  {{ parseFloat(ticket.totalAwarded || 0) > 0 ? setCurrencyString(ticket.totalAwarded) : '-' }}
                </span>
              </td>
              
              <td>
                <span class="currency-value">{{ setCurrencyString(ticket.commission) }}</span>
              </td>
              
              <td>
                <div class="action-buttons">
                  <button 
                    class="btn-action btn-view" 
                    @click="viewTicketDetails(ticket.betId)"
                    title="Ver detalhes"
                  >
                    <i class="fa-solid fa-eye"></i>
                  </button>
                  <button 
                    v-if="ticket.qrCode"
                    class="btn-action btn-qr" 
                    @click="showQRCode(ticket.qrCode)"
                    title="Ver PIN do Bilhete"
                  >
                    <i class="fa-solid fa-key"></i>
                  </button>
                  <button 
                    v-if="showRedeemButton(ticket)"
                    class="btn-action btn-redeem" 
                    @click="openRedeemModal(ticket)"
                    title="Resgatar Prêmio"
                  >
                    <i class="fa-solid fa-gift"></i>
                  </button>
                  <button 
                    v-if="!(ticket.canceled === true || ticket.canceled === 'true') && showCancelButton(ticket)"
                    class="btn-action btn-cancel" 
                    :class="{ 'btn-disabled': !canCancelTicket(ticket) }"
                    :disabled="!canCancelTicket(ticket)"
                    @click="confirmCancelTicket(ticket)"
                    :title="getCancelButtonTitle(ticket)"
                  >
                    <i v-if="!canCancelTicket(ticket)" class="fa-solid fa-clock"></i>
                    <i v-else class="fa-solid fa-times"></i>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Controles de Paginação Inferior -->
      <div v-if="totalPages > 1" class="pagination-footer">
        <div class="pagination-info">
          <span>Mostrando {{ startItem }}-{{ endItem }} de {{ filteredTickets.length }} bilhetes</span>
        </div>
        <div class="pagination-controls">
          <button 
            class="btn-pagination" 
            @click="changePage(0)"
            :disabled="currentPage === 0"
          >
            <i class="fa-solid fa-angles-left"></i>
          </button>
          <button 
            class="btn-pagination" 
            @click="changePage(currentPage - 1)"
            :disabled="currentPage === 0"
          >
            <i class="fa-solid fa-chevron-left"></i>
          </button>
          <span class="page-info">{{ currentPage + 1 }} / {{ totalPages }}</span>
          <button 
            class="btn-pagination" 
            @click="changePage(currentPage + 1)"
            :disabled="currentPage >= totalPages - 1"
          >
            <i class="fa-solid fa-chevron-right"></i>
          </button>
          <button 
            class="btn-pagination" 
            @click="changePage(totalPages - 1)"
            :disabled="currentPage >= totalPages - 1"
          >
            <i class="fa-solid fa-angles-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Estado vazio -->
    <div v-show="!loading && filteredTickets.length === 0" class="empty-state">
      <div class="empty-state-content">
        <i class="fa-solid fa-ticket"></i>
        <h3>Nenhum bilhete encontrado</h3>
        <p v-if="filters.status === 'canceled'">
          Não foram encontrados bilhetes cancelados no período selecionado.
        </p>
        <p v-else-if="filters.status === 'active'">
          Não foram encontrados bilhetes ativos no período selecionado.
        </p>
        <p v-else-if="filters.status === 'redeemed'">
          Não foram encontrados bilhetes com prêmios resgatados no período selecionado.
          <br><small>Bilhetes premiados só aparecem como "resgatados" após o vendedor efetuar o resgate do prêmio.</small>
        </p>
        <p v-else-if="filters.status === 'closed'">
          Não foram encontrados bilhetes encerrados no período selecionado.
          <br><small>Bilhetes são considerados "encerrados" quando já passou da data/hora do sorteio.</small>
        </p>
        <p v-else-if="filters.status === 'expired'">
          Não foram encontrados bilhetes com prêmios expirados no período selecionado.
          <br><small>Bilhetes premiados são considerados "expirados" quando o prazo de {{ getPrizeExpirationDays() }} dias para resgate do prêmio já passou (contados a partir da data do sorteio).</small>
        </p>
        <p v-else>
          Nenhum bilhete encontrado na região no período selecionado. Ajuste os filtros e tente novamente.
        </p>
      </div>
    </div>

    <!-- Modal de Detalhes do Bilhete -->
    <div v-if="showDetailsModal" class="modal-overlay" @click="closeDetailsModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>Detalhes do Bilhete</h3>
          <button class="btn-close" @click="closeDetailsModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="ticketDetails" class="ticket-details">
            <div class="detail-group">
              <label>NSU:</label>
              <span>{{ ticketDetails.nsu }}</span>
            </div>
            <div class="detail-group">
              <label>Vendedor:</label>
              <span>{{ ticketDetails.sellerName }}</span>
            </div>
            <div class="detail-group">
              <label>Status:</label>
              <span>{{ getStatusWithCancelDate(ticketDetails) }}</span>
            </div>
            <div class="detail-group">
              <label><i class="fa-solid fa-clock"></i> Data/Hora da Aposta:</label>
              <span>{{ formatDateTime(ticketDetails.betDateTimePersisted) }}</span>
            </div>
            <div class="detail-group">
              <label><i class="fa-solid fa-calendar-check"></i> Data do Sorteio:</label>
              <div class="draw-date-info">
                <div 
                  v-for="lottery in ticketDetails.lotteries" 
                  :key="lottery.lotteryDetailId"
                  class="lottery-draw-item"
                >
                  <span class="lottery-name">{{ lottery.lotteryDetailName }}</span>
                  <span class="draw-date">{{ formatTicketDrawDate(ticketDetails) }}</span>
                </div>
              </div>
            </div>
            <div class="detail-group">
              <label>Valor Total:</label>
              <span>{{ setCurrencyString(ticketDetails.amount) }}</span>
            </div>
            <div class="detail-group">
              <label>Comissão:</label>
              <span>{{ setCurrencyString(ticketDetails.commission) }}</span>
            </div>
            
            <!-- Informações de Prêmio -->
            <div v-if="getTotalPrizeValue(ticketDetails) > 0" class="detail-group">
              <label><i class="fa-solid fa-trophy"></i> Valor do Prêmio:</label>
              <span>{{ setCurrencyString(getTotalPrizeValue(ticketDetails)) }}</span>
            </div>
            
            <!-- Informações de Prêmio Resgatado -->
            <div v-if="isPrizeClaimedInDetails(ticketDetails)" class="detail-group">
              <label><i class="fa-solid fa-check-circle"></i> Status do Prêmio:</label>
              <span class="badge badge-success">
                <i class="fa-solid fa-check"></i> RESGATADO
              </span>
            </div>
            <div v-else-if="getTotalPrizeValue(ticketDetails) > 0" class="detail-group">
              <label><i class="fa-solid fa-clock"></i> Status do Prêmio:</label>
              <span class="badge badge-warning">
                <i class="fa-solid fa-clock"></i> NÃO RESGATADO
              </span>
            </div>
            
            <div v-if="ticketDetails.prizeClaimedDate" class="detail-group">
              <label><i class="fa-solid fa-calendar-check"></i> Data do Resgate:</label>
              <span>{{ formatDateTime(ticketDetails.prizeClaimedDate) }}</span>
            </div>
            <div v-if="ticketDetails.prizeClaimedBy" class="detail-group">
              <label><i class="fa-solid fa-user"></i> Resgatado por:</label>
              <span>{{ getSellerNameById(ticketDetails.prizeClaimedBy) }}</span>
            </div>
            
            <!-- Loterias e Números -->
            <div class="lotteries-section">
              <h4><i class="fa-solid fa-ticket"></i> Bilhetes e Números</h4>
              <div
                v-for="lottery in ticketDetails.lotteries"
                :key="lottery.lotteryDetailId"
                class="lottery-item"
              >
                <div class="lottery-header">
                  <div class="lottery-title">
                    <i class="fa-solid fa-clover"></i>
                    <h5>{{ lottery.lotteryDetailName }}</h5>
                  </div>
                  <div class="lottery-draw-date">
                    <i class="fa-solid fa-calendar-check"></i>
                    <span>{{ formatTicketDrawDate(ticketDetails) }}</span>
                  </div>
                </div>

                <!-- Agrupar números por grupo de aposta (betGroupOrder) primeiro, depois por modalidade -->
                <div
                  v-for="group in groupNumbersByBetGroup(lottery.numbers)"
                  :key="group.groupOrder"
                  class="bet-group"
                >
                  <!-- Cabeçalho do grupo: JOGO PRINCIPAL -->
                  <div class="bet-group-header">
                    <div class="bet-group-title">
                      <i class="fa-solid fa-layer-group"></i>
                      <span>JOGO PRINCIPAL</span>
                    </div>
                  </div>

                  <!-- Dentro do mesmo grupo, exibir todas as modalidades -->
                  <div
                    v-for="(modalityNumbers, modalityIndex) in getModalitiesInGroup(group.numbers)"
                    :key="group.groupOrder + '-' + modalityIndex"
                    class="modality-group"
                  >
                    <div class="modality-header">
                      <div class="modality-name-container">
                        <span class="modality-name">{{ formatModalityLabel(modalityNumbers[0]) }}</span>
                        <!-- Mostrar "+ VALENDO" quando plusValueType > 0 (0 = JOGO PRINCIPAL, 1+ = + VALENDO) -->
                        <span v-if="(modalityNumbers[0].plusValueType || 0) > 0" class="valendo-badge">+ VALENDO</span>
                      </div>
                      <span class="modality-placing">{{ modalityNumbers[0].placing }}</span>
                    </div>

                    <div class="numbers-container">
                      <div
                        v-for="(number, numIdx) in modalityNumbers"
                        :key="`${group.groupOrder}-${modalityIndex}-${numIdx}-${number.number}`"
                        class="number-badge"
                        :class="{
                          'number-awarded': number.numberAwards && number.numberAwards.length > 0,
                          'number-badge-combo': isCombinedNumber(number.number)
                        }"
                      >
                        <div
                          v-if="isCombinedNumber(number.number)"
                          class="combo-dezenas"
                        >
                          <span
                            v-for="(dez, dezIdx) in splitCombinedNumber(number.number)"
                            :key="dezIdx"
                            class="dezena-chip"
                          >{{ dez }}</span>
                        </div>
                        <div v-else class="number-value">{{ number.number }}</div>
                        <div class="number-meta">
                          <span v-if="isCombinedNumber(number.number)" class="combo-count">
                            {{ splitCombinedNumber(number.number).length }} dezenas
                          </span>
                          <div class="number-amount">{{ setCurrencyString(number.value) }}</div>
                        </div>
                        <div v-if="number.numberAwards && number.numberAwards.length > 0" class="number-award-indicator">
                          <i class="fa-solid fa-trophy"></i>
                          <span>{{ setCurrencyString(getTotalAwardValue(number.numberAwards)) }}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de PIN do Bilhete -->
    <div v-if="showQRModal" class="modal-overlay" @click="closeQRModal">
      <div class="modal-content pin-modal" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-key"></i>
            PIN do Bilhete
          </h3>
          <button class="btn-close" @click="closeQRModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="pin-code-container">
            <div class="pin-code-info">
              <div class="pin-code-label">
                <i class="fa-solid fa-shield-halved"></i>
                Código de Segurança
              </div>
              <div class="pin-code-display">{{ selectedQRCode }}</div>
              <div class="pin-code-instructions">
                <i class="fa-solid fa-info-circle"></i>
                Este PIN é usado para validar e resgatar prêmios do bilhete
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal de Confirmação de Cancelamento -->
    <div v-if="showCancelModal" class="modal-overlay" @click="closeCancelModal">
      <div class="modal-content cancel-modal" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-exclamation-triangle"></i>
            Confirmar Cancelamento
          </h3>
          <button class="btn-close" @click="closeCancelModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="ticketToCancel" class="cancel-confirmation">
            <div class="warning-message">
              <i class="fa-solid fa-warning"></i>
              <div class="warning-content">
                <h4>Atenção!</h4>
                <p>Você está prestes a cancelar o bilhete com NSU <strong>{{ ticketToCancel.nsu }}</strong>.</p>
                <p>Esta ação <strong>não pode ser desfeita</strong>.</p>
              </div>
            </div>
            
            <div class="ticket-info">
              <div class="info-row">
                <span class="info-label">NSU:</span>
                <span class="info-value">{{ ticketToCancel.nsu }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Valor:</span>
                <span class="info-value">{{ setCurrencyString(ticketToCancel.amount) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Data/Hora:</span>
                <span class="info-value">{{ formatDateTime(ticketToCancel.dateTimePersisted) }}</span>
              </div>
              <div v-if="getTimeRemaining(ticketToCancel)" class="info-row">
                <span class="info-label">Tempo para cancelar:</span>
                <span class="info-value time-remaining">{{ getTimeRemaining(ticketToCancel) }}</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            @click="closeCancelModal"
            :disabled="cancelLoading"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn-primary" 
            @click="processCancelTicket"
            :disabled="cancelLoading"
          >
            <i v-if="cancelLoading" class="fa-solid fa-spinner fa-spin"></i>
            {{ cancelLoading ? 'Cancelando...' : 'Confirmar Cancelamento' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Resgate de Prêmio -->
    <div v-if="showRedeemModal" class="modal-overlay" @click="closeRedeemModal">
      <div class="modal-content redeem-modal" @click.stop>
        <div class="modal-header">
          <h3>
            <i class="fa-solid fa-gift"></i>
            Resgatar Prêmio
          </h3>
          <button class="btn-close" @click="closeRedeemModal">
            <i class="fa-solid fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="ticketToRedeem" class="redeem-content">
            <div class="success-message">
              <i class="fa-solid fa-trophy"></i>
              <div class="success-content">
                <h4>Bilhete Premiado!</h4>
                <p>Digite o NSU ou PIN do bilhete para confirmar o resgate do prêmio.</p>
              </div>
            </div>
            
            <div class="ticket-info">
              <div class="info-row">
                <span class="info-label">NSU:</span>
                <span class="info-value">{{ ticketToRedeem.nsu }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Valor do Prêmio:</span>
                <span class="info-value prize-value">{{ setCurrencyString(ticketToRedeem.totalAwarded) }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">Data do Sorteio:</span>
                <span class="info-value">{{ formatDate(ticketToRedeem.dateTime || ticketToRedeem.betDateTime) }}</span>
              </div>
              <div v-if="!isPrizeExpired(ticketToRedeem)" class="info-row">
                <span class="info-label">Validade:</span>
                <span class="info-value validity-ok">
                  <i class="fa-solid fa-check-circle"></i>
                  Dentro do prazo ({{ getPrizeExpirationDays() }} dias)
                </span>
              </div>
            </div>

            <div class="input-group-redeem">
              <label class="input-label">
                <i class="fa-solid fa-key"></i>
                NSU ou PIN do Bilhete
              </label>
              <input 
                class="form-input" 
                type="text" 
                v-model="redeemPin" 
                placeholder="Digite o NSU ou PIN"
                @keyup.enter="processRedeemPrize"
                :disabled="redeemLoading"
              />
              <small class="input-hint">
                Digite o NSU ({{ ticketToRedeem.nsu }}) ou o PIN do bilhete para confirmar.
              </small>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button 
            type="button" 
            class="btn-secondary" 
            @click="closeRedeemModal"
            :disabled="redeemLoading"
          >
            <i class="fa-solid fa-times"></i>
            Cancelar
          </button>
          <button 
            type="button" 
            class="btn-success" 
            @click="processRedeemPrize"
            :disabled="redeemLoading || !redeemPin"
          >
            <i v-if="redeemLoading" class="fa-solid fa-spinner fa-spin"></i>
            <i v-else class="fa-solid fa-gift"></i>
            {{ redeemLoading ? 'Resgatando...' : 'Confirmar Resgate' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import mainService from "@/services/mainService";
import { toast } from "@/composables/useToast";
import { handleErrorMessage } from "@/services/config";
import jsFunctions from "@/services/jsFunctions";
import { ROLES } from "@/services/permissions";
import { useRegion } from "@/composables/useRegion";
import { groupBingoSemanalTickets } from "@/utils/bingoSemanalTickets";

export default {
  name: "ViewSellerTickets",
  setup() {
    const { isRegionReady, waitForRegion, getStoredRegion } = useRegion();
    return { isRegionReady, waitForRegion, getStoredRegion };
  },
  data() {
    return {
      loading: false,
      allTickets: [], // Array original dos bilhetes
      tickets: [], // Array filtrado para exibição
      sellers: [], // Sempre inicializar como array vazio
      lotteries: [], // Lista de loterias disponíveis
      filters: {
        startDate: '',
        endDate: '',
        sellerId: '',
        status: '',
        lotteryId: '',
        lotteryDetailId: '',
        search: ''
      },
      showDetailsModal: false,
      showQRModal: false,
      ticketDetails: null,
      selectedQRCode: '',
      userRegion: null,
      currentUser: {},
      
      // Paginação
      currentPage: 0,
      itemsPerPage: 10,

      // Controle de usuário e permissões
      userResponsibleRegions: [],
      bancas: [],
      regions: [],
      currentRegionData: null, // Dados completos da região atual

      // Modal de confirmação de cancelamento
      showCancelModal: false,
      ticketToCancel: null,
      cancelLoading: false,

      // Modal de resgate de prêmio
      showRedeemModal: false,
      ticketToRedeem: null,
      redeemPin: '',
      redeemLoading: false,
      filterWatchReady: false,
      searchTimer: null
    };
  },
  
  computed: {
    // Bilhetes ativos para resumo dos cards (exclui cancelados)
    activeTicketsForSummary() {
      if (!Array.isArray(this.filteredTickets)) return [];
      return this.filteredTickets.filter(t => t.canceled !== true && t.canceled !== 'true');
    },

    // Calcular totais (desconsidera bilhetes cancelados)
    totalBetsAmount() {
      return this.activeTicketsForSummary.reduce((sum, ticket) => sum + parseFloat(ticket.amount || 0), 0);
    },
    
    totalCommission() {
      return this.activeTicketsForSummary.reduce((sum, ticket) => sum + parseFloat(ticket.commission || 0), 0);
    },
    
    totalPrizes() {
      return this.activeTicketsForSummary.reduce((sum, ticket) => sum + parseFloat(ticket.totalAwarded || 0), 0);
    },
    
    // Paginação
    totalPages() {
      if (!Array.isArray(this.filteredTickets)) return 0;
      return Math.ceil(this.filteredTickets.length / this.itemsPerPage);
    },
    
    paginatedTickets() {
      if (!Array.isArray(this.filteredTickets)) {
        return [];
      }
      const start = this.currentPage * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredTickets.slice(start, end);
    },
    
    startItem() {
      return (this.currentPage * this.itemsPerPage) + 1;
    },
    
    endItem() {
      if (!Array.isArray(this.filteredTickets)) return 0;
      return Math.min((this.currentPage + 1) * this.itemsPerPage, this.filteredTickets.length);
    },
    
    selectedSellerName() {
      if (!this.filters.sellerId) return '';
      const seller = this.sellers.find(s => s.id === this.filters.sellerId);
      return seller ? seller.name : '';
    },

    isAdmin() {
      return this.currentUser && this.currentUser.role === ROLES.ADMIN;
    },

    isBanca() {
      return this.currentUser && this.currentUser.role === ROLES.BANCA;
    },

    isGerente() {
      return this.currentUser && this.currentUser.role === ROLES.GERENTE;
    },

    isCobrador() {
      return this.currentUser && this.currentUser.role === ROLES.COBRADOR;
    },

    availableBancasForUser() {
      if (!this.bancas || !Array.isArray(this.bancas)) {
        return [];
      }
      
      if (this.isAdmin) {
        // ADMIN vê todas as bancas
        return this.bancas;
      } else if (this.isBanca && this.currentUser) {
        // BANCA vê apenas ela mesma
        return this.bancas.filter(banca => 
          banca && banca.responsibleUserIds && Array.isArray(banca.responsibleUserIds) &&
          banca.responsibleUserIds.includes(this.currentUser.id)
        );
      } else {
        // GERENTE e COBRADOR: mostrar bancas das regiões onde eles atuam
        return this.bancas.filter(banca => {
          if (!banca || !banca.regionIds || !Array.isArray(banca.regionIds)) return false;
          if (!this.userResponsibleRegions || !Array.isArray(this.userResponsibleRegions)) return false;
          
          return banca.regionIds.some(regionId => 
            regionId && this.userResponsibleRegions.includes(regionId)
          );
        });
      }
    },

    filteredSellers() {
      if (!this.sellers || !Array.isArray(this.sellers)) {
        return [];
      }
      
      let sellers = this.sellers;
      
      // Aplicar filtro baseado no role do usuário
      if (this.isCobrador) {
        // COBRADOR: apenas vendedores vinculados (já carregados via GetSellersByCollector).
        // Se houver região no cabeçalho, restringe também à região ativa.
        const collectorId = this.normalizeId(this.currentUser?.id);
        sellers = sellers.filter(seller => {
          if (!seller || !seller.id) return false;
          const sellerCollector = this.normalizeId(
            seller.collectorId || seller.CollectorId || collectorId,
          );
          // Lista já veio da API de vínculo; garante collectorId quando presente
          if (
            seller.collectorId != null ||
            seller.CollectorId != null
          ) {
            if (sellerCollector !== collectorId) return false;
          }
          if (this.userRegion) {
            return (
              seller.regionId &&
              this.normalizeId(seller.regionId) === this.normalizeId(this.userRegion)
            );
          }
          return true;
        });
      } else if (this.isBanca && this.userRegion) {
        // BANCA vê apenas vendedores da região selecionada no cabeçalho
        sellers = sellers.filter(seller => 
          seller && seller.regionId && seller.regionId === this.userRegion
        );
      } else if (!this.isAdmin) {
        // GERENTE vê apenas vendedores de suas regiões
        if (this.userResponsibleRegions && Array.isArray(this.userResponsibleRegions)) {
          sellers = sellers.filter(seller => 
            seller && seller.regionId && this.userResponsibleRegions.includes(seller.regionId)
          );
        }
      }
      
      return sellers || [];
    },

    /** IDs dos vendedores que o cobrador pode ver (vinculados). */
    cobradorLinkedSellerIds() {
      if (!this.isCobrador) return null;
      const ids = new Set();
      (this.filteredSellers || []).forEach(s => {
        const id = this.normalizeId(s?.id);
        if (id) ids.add(id);
      });
      return ids;
    },

    // Verificar se a região atual é de descarga
    currentSelectedRegion() {
      const savedRegion = localStorage.getItem('currentRegion');
      return savedRegion ? JSON.parse(savedRegion) : null;
    },

    isDescargaRegion() {
      return this.currentSelectedRegion && this.currentSelectedRegion.onlyDescarga;
    },

    // Detalhes da loteria (horários) disponíveis para a loteria selecionada
    availableLotteryDetails() {
      if (!this.filters.lotteryId || !this.lotteries || this.lotteries.length === 0) {
        return [];
      }
      
      const selectedLottery = this.lotteries.find(l => l.id === this.filters.lotteryId);
      return selectedLottery && selectedLottery.details ? selectedLottery.details : [];
    },

    // Filtrar tickets: data, vendedor, status, loteria e horário juntos (AND)
    filteredTickets() {
      if (!Array.isArray(this.allTickets)) {
        return [];
      }

      const startDate = this.filters.startDate || '';
      const endDate = this.filters.endDate || '';
      const sellerId = this.normalizeId(this.filters.sellerId);
      const lotteryId = this.normalizeId(this.filters.lotteryId);
      const lotteryDetailId = this.normalizeId(this.filters.lotteryDetailId);
      const status = this.filters.status || '';
      const search = String(this.filters.search || '').trim().toLowerCase();

      return this.allTickets.filter(ticket => {
        if (startDate || endDate) {
          const ticketDate = this.ticketDateKey(ticket);
          if (!ticketDate) return false;
          if (startDate && ticketDate < startDate) return false;
          if (endDate && ticketDate > endDate) return false;
        }

        if (sellerId) {
          const ticketSeller = this.normalizeId(ticket.sellerId || ticket.SellerId);
          if (ticketSeller !== sellerId) return false;
        }

        // COBRADOR: nunca exibe bilhete de vendedor não vinculado
        if (this.isCobrador && this.cobradorLinkedSellerIds) {
          const ticketSeller = this.normalizeId(ticket.sellerId || ticket.SellerId);
          if (!ticketSeller || !this.cobradorLinkedSellerIds.has(ticketSeller)) {
            return false;
          }
        }

        if (search) {
          const nsu = String(ticket.nsu || ticket.Nsu || '').toLowerCase();
          const pin = String(
            ticket.qrCode || ticket.QrCode || ticket.pin || ticket.Pin || ''
          ).toLowerCase();
          if (!nsu.includes(search) && !pin.includes(search)) return false;
        }

        const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
        const totalAwarded = parseFloat(ticket.totalAwarded || ticket.TotalAwarded || 0);
        const isClosed = this.isTicketClosed(ticket);
        const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === 'true';

        if (status) {
          let statusMatch = true;
          switch (status) {
            case 'canceled':
              statusMatch = isCanceled;
              break;
            case 'awarded':
              statusMatch = totalAwarded > 0 && !isCanceled;
              break;
            case 'redeemed':
              statusMatch = totalAwarded > 0 && isPrizeClaimed && !isCanceled;
              break;
            case 'closed':
              statusMatch = isClosed;
              break;
            case 'expired':
              statusMatch = this.isPrizeExpired(ticket) && totalAwarded > 0 && !isPrizeClaimed && !isCanceled;
              break;
            case 'active':
              statusMatch = !isCanceled && totalAwarded === 0 && !isClosed;
              break;
            default:
              statusMatch = true;
          }
          if (!statusMatch) return false;
        }

        const lotteries = ticket.lotteries || ticket.Lotteries;
        if (lotteryId || lotteryDetailId) {
          if (!Array.isArray(lotteries) || lotteries.length === 0) return false;
          const hasMatch = lotteries.some(lottery => {
            const lid = this.normalizeId(lottery.lotteryId || lottery.LotteryId);
            const did = this.normalizeId(lottery.lotteryDetailId || lottery.LotteryDetailId);
            if (lotteryId && lid !== lotteryId) return false;
            if (lotteryDetailId && did !== lotteryDetailId) return false;
            return true;
          });
          if (!hasMatch) return false;
        }

        return true;
      });
    }
  },
  
  async mounted() {
    // Aguardar região estar pronta antes de carregar dados
    const regionReady = await this.waitForRegion(10000); // timeout de 10 segundos

    if (!regionReady && !this.getStoredRegion()) {
      toast.warning("Aguardando seleção de região...");
      // Continuar carregando os dados básicos mesmo sem região
    }

    await this.loadCurrentUser();
    await this.loadBancas();
    await this.loadRegions();
    await this.loadLotteries();
    await this.loadCurrentRegionData(); // Carregar dados da região atual

    // Para usuários BANCA, complementar regiões responsáveis com regiões das bancas
    if (this.isBanca) {
      await this.loadBancaRegions();
    }

    this.initializeUser();
    this.initializeFilters();

    // Só carregar dados iniciais se a região estiver pronta
    if (this.isRegionReady || this.getStoredRegion()) {
      await this.loadInitialData();
    }
    this.filterWatchReady = true;

    // Listener para mudanças na região selecionada
    this.handleStorageChange = async (e) => {
      if (e.key === 'currentRegion') {
        // Atualizar userRegion com a nova região selecionada
        const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
        this.userRegion = currentRegion ? currentRegion.id : null;
        
        // Recarregar dados da região quando mudar
        await this.loadCurrentRegionData();
        
        // Recarregar vendedores quando a região mudar (especialmente importante para BANCA)
        await this.loadSellers();
        
        // Recarregar bilhetes se houver filtros aplicados
        if (this.filters.startDate && this.filters.endDate) {
          await this.searchTickets();
        }
        
        // Forçar reatividade na computed property
        this.$forceUpdate();
      }
    };
    window.addEventListener('storage', this.handleStorageChange);
  },

  beforeUnmount() {
    // Remover listener ao destruir o componente
    window.removeEventListener('storage', this.handleStorageChange);
    if (this.searchTimer) {
      clearTimeout(this.searchTimer);
      this.searchTimer = null;
    }
  },

  watch: {
    'filters.startDate'() {
      this.scheduleSearchFromFilters();
    },
    'filters.endDate'() {
      this.scheduleSearchFromFilters();
    },
    'filters.sellerId'() {
      this.scheduleSearchFromFilters();
    },
    'filters.status'() {
      this.currentPage = 0;
    },
    'filters.lotteryId'() {
      this.currentPage = 0;
    },
    'filters.lotteryDetailId'() {
      this.currentPage = 0;
    },
    'filters.search'() {
      this.currentPage = 0;
    },

    // Observar mudanças na região para resetar filtro se necessário
    isDescargaRegion(newValue) {
      // Se mudou para região de descarga e estava filtrado como "redeemed", resetar para "awarded"
      if (newValue && this.filters.status === 'redeemed') {
        this.filters.status = 'awarded';
      }
    }
  },
  
  methods: {
    normalizeId(value) {
      return value == null || value === '' ? '' : String(value).toLowerCase();
    },

    ticketDateKey(ticket) {
      const raw =
        ticket?.dateTimePersisted ||
        ticket?.DateTimePersisted ||
        ticket?.betDateTimePersisted ||
        ticket?.BetDateTimePersisted ||
        '';
      const str = String(raw);
      const isoDay = str.match(/^(\d{4}-\d{2}-\d{2})/);
      if (isoDay) return isoDay[1];
      const d = new Date(str);
      if (Number.isNaN(d.getTime())) return '';
      const pad = (n) => String(n).padStart(2, '0');
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },

    scheduleSearchFromFilters() {
      this.currentPage = 0;
      if (!this.filterWatchReady) return;
      if (this.searchTimer) clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        if (this.filters.startDate && this.filters.endDate) {
          this.searchTickets();
        }
      }, 350);
    },
    async loadCurrentUser() {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          this.currentUser = JSON.parse(userData);
          
          // Se não for ADMIN, carregar regiões do usuário responsável  
          if (this.currentUser.role !== 0) {
            await this.loadUserResponsibleRegions();
          }
        }
      } catch (error) {
        this.currentUser = {};
        toast.error("Erro ao carregar dados do usuário");
      }
    },

    async loadUserResponsibleRegions() {
      try {
        if (!this.currentUser) return;
        
        const response = await mainService.getUserRegions(this.currentUser.id);
        this.userResponsibleRegions = response.data.map(region => region.id);
        
        // Incluir a região principal também, se existir
        if (this.currentUser.regionId && !this.userResponsibleRegions.includes(this.currentUser.regionId)) {
          this.userResponsibleRegions.push(this.currentUser.regionId);
        }
      } catch (error) {
        this.userResponsibleRegions = [];
        toast.error("Erro ao carregar regiões do usuário");
      }
    },

    async loadBancas() {
      try {
        const response = await mainService.searchBancas({ isActive: true });
        this.bancas = response.data || [];
      } catch (error) {
        this.bancas = [];
      }
    },

    async loadRegions() {
      try {
        const response = await mainService.searchRegions();
        this.regions = response.data || [];
      } catch (error) {
        this.regions = [];
      }
    },

    async loadLotteries() {
      try {
        const response = await mainService.searchLotteries();
        const allLotteries = response.data || [];
        
        // Filtrar apenas loterias ativas na região atual
        if (this.userRegion) {
          try {
            const regionConfigsResponse = await mainService.searchRegionConfigs();
            if (regionConfigsResponse.data && regionConfigsResponse.data.regionLotteriesParameter) {
              const regionParams = regionConfigsResponse.data.regionLotteriesParameter;
              const activeLotteryDetailIds = new Set(
                regionParams
                  .filter(param => param.isActive)
                  .map(param => param.lotteryDetailId)
              );
              
              // Filtrar loterias que têm pelo menos um horário ativo na região
              this.lotteries = allLotteries.map(lottery => {
                if (lottery.details && Array.isArray(lottery.details)) {
                  const activeDetails = lottery.details.filter(detail => 
                    activeLotteryDetailIds.has(detail.id)
                  );
                  
                  if (activeDetails.length > 0) {
                    return {
                      ...lottery,
                      details: activeDetails
                    };
                  }
                }
                return null;
              }).filter(lottery => lottery !== null);
            } else {
              this.lotteries = allLotteries;
            }
          } catch (error) {
            // Se falhar ao buscar configurações, usar todas as loterias
            this.lotteries = allLotteries;
          }
        } else {
          this.lotteries = allLotteries;
        }
      } catch (error) {
        this.lotteries = [];
      }
    },

    async loadCurrentRegionData() {
      try {
        const currentRegion = localStorage.getItem('currentRegion');
        if (!currentRegion) {
          this.currentRegionData = null;
          return;
        }

        const region = JSON.parse(currentRegion);
        
        if (!region || !region.id) {
          this.currentRegionData = null;
          return;
        }

        const response = await mainService.getRegionById(region.id);
        
        if (response.data) {
          this.currentRegionData = response.data;
        }
      } catch (error) {
        this.currentRegionData = null;
      }
    },

    onLotteryChange() {
      // Quando a loteria muda, resetar o filtro de hora do sorteio
      this.filters.lotteryDetailId = '';
      // Resetar paginação
      this.currentPage = 0;
    },

    async loadBancaRegions() {
      try {
        if (!this.currentUser || !this.isBanca) return;
        if (!this.bancas || this.bancas.length === 0) return;
        
        // Buscar bancas disponíveis para o usuário BANCA
        const bancasForUser = this.availableBancasForUser;
        
        if (bancasForUser && Array.isArray(bancasForUser)) {
          for (const banca of bancasForUser) {
            if (banca && banca.regionIds && Array.isArray(banca.regionIds)) {
              // Adicionar todas as regiões desta banca às regiões responsáveis
              banca.regionIds.forEach(regionId => {
                if (regionId && !this.userResponsibleRegions.includes(regionId)) {
                  this.userResponsibleRegions.push(regionId);
                }
              });
            }
          }
        }
      } catch (error) {
        // Ignorar erro
      }
    },
    
    initializeUser() {
      // Obter dados do usuário logado
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          this.currentUser = JSON.parse(userStr);
        } catch (e) {
          this.currentUser = {};
        }
      }
      
      // Obter região atual
      const currentRegion = JSON.parse(localStorage.getItem('currentRegion') || 'null');
      this.userRegion = currentRegion ? currentRegion.id : null;
    },
    
    initializeFilters() {
      // Definir data de hoje como padrão
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      this.filters.startDate = todayStr;
      this.filters.endDate = todayStr;
    },
    
    async loadInitialData() {
      // Carregar vendedores da região
      await this.loadSellers();
      // Carregar bilhetes de hoje automaticamente
      if (this.userRegion && this.filters.startDate && this.filters.endDate) {
        await this.searchTickets();
      }
    },
    
    async loadSellers() {
      if (this.isAdmin) {
        // ADMIN busca todos os vendedores
        try {
          const response = await mainService.searchSellers();
          this.sellers = (response.data || []).map(seller => {
            // Buscar a região do vendedor para obter a banca
            const sellerRegion = (this.regions && Array.isArray(this.regions)) ? 
              this.regions.find(r => r && r.id === seller.regionId) : null;
            const sellerBanca = (sellerRegion && this.bancas && Array.isArray(this.bancas)) ? 
              this.bancas.find(b => b && b.id === sellerRegion.bancaId) : null;
            
            return {
              ...seller,
              bancaId: sellerBanca?.id || null,
              bancaName: sellerBanca?.name || '-',
            };
          });
        } catch (error) {
          this.sellers = [];
        }
      } else if (this.isCobrador && this.currentUser?.id) {
        // COBRADOR: apenas vendedores vinculados a ele
        try {
          const response = await mainService.getSellersByCollector(this.currentUser.id);
          const list = Array.isArray(response.data) ? response.data : [];
          this.sellers = list.map(seller => {
            const sellerRegion = (this.regions && Array.isArray(this.regions)) ?
              this.regions.find(r => r && r.id === seller.regionId) : null;
            const sellerBanca = (sellerRegion && this.bancas && Array.isArray(this.bancas)) ?
              this.bancas.find(b => b && b.id === sellerRegion.bancaId) : null;

            return {
              ...seller,
              collectorId: seller.collectorId || seller.CollectorId || this.currentUser.id,
              bancaId: sellerBanca?.id || null,
              bancaName: sellerBanca?.name || '-',
            };
          });

          // Se filtro atual aponta para vendedor não vinculado, limpa
          if (
            this.filters.sellerId &&
            !this.sellers.some(s => this.normalizeId(s.id) === this.normalizeId(this.filters.sellerId))
          ) {
            this.filters.sellerId = '';
          }
        } catch (error) {
          this.sellers = [];
          toast.error('Erro ao carregar vendedores vinculados ao cobrador');
        }
      } else if (this.isBanca && this.userRegion) {
        // BANCA busca vendedores apenas da região selecionada no cabeçalho
        try {
          const response = await mainService.searchSellersByRegion(this.userRegion);
          this.sellers = (response.data || []).map(seller => {
            // Buscar a região do vendedor para obter a banca
            const sellerRegion = (this.regions && Array.isArray(this.regions)) ? 
              this.regions.find(r => r && r.id === seller.regionId) : null;
            const sellerBanca = (sellerRegion && this.bancas && Array.isArray(this.bancas)) ? 
              this.bancas.find(b => b && b.id === sellerRegion.bancaId) : null;
            
            return {
              ...seller,
              bancaId: sellerBanca?.id || null,
              bancaName: sellerBanca?.name || '-',
            };
          });
        } catch (error) {
          this.sellers = [];
          toast.error("Erro ao carregar vendedores da região selecionada");
        }
      } else {
        // GERENTE busca vendedores de suas regiões
        if (this.userResponsibleRegions.length > 0) {
          try {
            const promises = this.userResponsibleRegions.map(regionId => 
              mainService.searchSellersByRegion(regionId)
            );
            
            const responses = await Promise.all(promises);
            
            // Combinar resultados de todas as regiões
            let allSellers = [];
            responses.forEach(response => {
              if (response.data && Array.isArray(response.data)) {
                allSellers = allSellers.concat(response.data);
              }
            });
            
            // Remover duplicatas baseado no ID e adicionar informações de banca
            const uniqueSellers = allSellers.filter((seller, index, self) => 
              index === self.findIndex(s => s.id === seller.id)
            ).map(seller => {
              // Buscar a região do vendedor para obter a banca
              const sellerRegion = (this.regions && Array.isArray(this.regions)) ? 
                this.regions.find(r => r && r.id === seller.regionId) : null;
              const sellerBanca = (sellerRegion && this.bancas && Array.isArray(this.bancas)) ? 
                this.bancas.find(b => b && b.id === sellerRegion.bancaId) : null;
              
              return {
                ...seller,
                bancaId: sellerBanca?.id || null,
                bancaName: sellerBanca?.name || '-',
              };
            });
            
            this.sellers = uniqueSellers;
          } catch (error) {
            this.sellers = [];
          }
        } else {
          this.sellers = [];
          toast.warning("Nenhuma região definida para o usuário!");
        }
      }
    },
    
    async searchTickets() {
      if (!this.userRegion) {
        toast.error('Selecione uma região no cabeçalho do sistema');
        return;
      }
      
      if (!this.filters.startDate || !this.filters.endDate) {
        toast.error('Informe as datas inicial e final');
        return;
      }

      // Validar se data início não é maior que data fim
      if (new Date(this.filters.startDate) > new Date(this.filters.endDate)) {
        toast.error('A data de início não pode ser maior que a data de fim');
        return;
      }
      
      this.loading = true;
      this.currentPage = 0; // Reset pagination
      
      try {
        // COBRADOR não pode consultar vendedor que não está vinculado a ele
        if (this.isCobrador) {
          const allowed = this.cobradorLinkedSellerIds;
          if (!allowed || allowed.size === 0) {
            this.allTickets = [];
            toast.info('Nenhum vendedor vinculado ao seu usuário de cobrador');
            return;
          }
          if (
            this.filters.sellerId &&
            !allowed.has(this.normalizeId(this.filters.sellerId))
          ) {
            this.filters.sellerId = '';
            toast.warning('Vendedor selecionado não está vinculado a você');
          }
        }

        const ticketsData = await this.fetchAllTicketsForPeriod({
          regionId: this.userRegion,
          sellerId: this.filters.sellerId || null,
          startDate: this.filters.startDate,
          endDate: this.filters.endDate,
        });
        
        this.allTickets = groupBingoSemanalTickets(
          Array.isArray(ticketsData) ? ticketsData : [],
        );
        this.allTickets.sort((a, b) => new Date(b.dateTimePersisted) - new Date(a.dateTimePersisted));
        
        if (this.allTickets.length === 0) {
          toast.info('Nenhum bilhete encontrado no período selecionado');
        }
        
      } catch (error) {
        toast.error(handleErrorMessage(error));
        this.allTickets = [];
      } finally {
        this.loading = false;
      }
    },

    /**
     * A API devolve no máx. 1000 bilhetes por página (mais recentes primeiro).
     * Sem paginar todas as páginas, períodos longos parecem "só os últimos dias".
     */
    async fetchAllTicketsForPeriod({ regionId, sellerId, startDate, endDate }) {
      const pageSize = 1000;
      const maxPages = 200;
      const all = [];
      let pageIndex = 1;
      let totalPages = null;

      while (pageIndex <= maxPages) {
        const params = new URLSearchParams({
          regionId: String(regionId),
          startDate: String(startDate),
          endDate: String(endDate),
          pageIndex: String(pageIndex),
        });

        let response;
        if (sellerId) {
          params.set("sellerId", String(sellerId));
          response = await mainService.searchBetsBySeller(params.toString());
        } else {
          response = await mainService.searchBalanceBets(params.toString());
        }

        const page = this.extractBetSearchPage(response);
        if (page.totalPages != null) totalPages = page.totalPages;
        all.push(...page.items);

        if (!page.items.length) break;
        if (totalPages != null && pageIndex >= totalPages) break;
        if (page.items.length < pageSize) break;
        pageIndex += 1;
      }

      return all;
    },

    extractBetSearchPage(response) {
      const data = response?.data;
      if (!data) return { items: [], totalPages: null };

      // UseCaseResponse: { result, totalPages, totalRows }
      if (Array.isArray(data.result)) {
        return {
          items: data.result,
          totalPages: data.totalPages ?? data.TotalPages ?? null,
        };
      }
      // Lista direta (legado / ActionResultConverter)
      if (Array.isArray(data)) {
        return { items: data, totalPages: null };
      }
      return { items: [], totalPages: null };
    },

    
    // Paginação
    changePage(newPage) {
      if (newPage >= 0 && newPage < this.totalPages) {
        this.currentPage = newPage;
      }
    },
    
    async viewTicketDetails(betId) {
      try {
        const response = await mainService.searchBetById(betId);
        this.ticketDetails = response.data;
        this.showDetailsModal = true;
      } catch (error) {
        toast.error('Erro ao carregar detalhes do bilhete');
      }
    },
    
    showQRCode(qrCode) {
      this.selectedQRCode = qrCode;
      this.showQRModal = true;
    },
    
    closeDetailsModal() {
      this.showDetailsModal = false;
      this.ticketDetails = null;
    },
    
    closeQRModal() {
      this.showQRModal = false;
      this.selectedQRCode = '';
    },
    
    getStatusClass(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === 'true';
      const isExpired = this.isPrizeExpired(ticket);
      
      if (isCanceled) return 'status-canceled';
      if (totalAwarded > 0 && isExpired && !isPrizeClaimed) return 'status-expired';
      if (totalAwarded > 0) return 'status-awarded';
      if (isClosed) return 'status-closed';
      return 'status-active';
    },
    
    getStatusIcon(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === 'true';
      const isExpired = this.isPrizeExpired(ticket);
      
      if (isCanceled) return 'fa-solid fa-times-circle';
      if (totalAwarded > 0 && isExpired && !isPrizeClaimed) return 'fa-solid fa-clock';
      if (totalAwarded > 0) return 'fa-solid fa-trophy';
      if (isClosed) return 'fa-solid fa-lock';
      return 'fa-solid fa-check-circle';
    },
    
    getStatusText(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === 'true';
      const isExpired = this.isPrizeExpired(ticket);
      
      if (isCanceled) return 'Cancelado';
      if (totalAwarded > 0 && isExpired && !isPrizeClaimed) return 'Expirado';
      if (totalAwarded > 0) return 'Premiado';
      if (isClosed) return 'Encerrado';
      return 'Ativo';
    },
    
    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR');
    },
    
    formatTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    },
    
    formatDateTime(dateString) {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR');
    },

    // Bingo Semanal (vários sorteios): "primeiro a último"; demais: data única
    formatTicketDrawDate(details) {
      if (!details) return '';
      const count = Number(details.packageDrawCount || details.PackageDrawCount || 1) || 1;
      const first =
        details.packageFirstDrawDateTime ||
        details.PackageFirstDrawDateTime ||
        details.betDateTime;
      const last =
        details.packageLastDrawDateTime ||
        details.PackageLastDrawDateTime;

      if (count > 1 && first && last) {
        return `${this.formatDateTime(first)} a ${this.formatDateTime(last)}`;
      }

      return this.formatDate(details.betDateTime);
    },
    
    setCurrencyString(value) {
      return jsFunctions.setCurrencyString(value);
    },

    // Verificar se o bilhete está encerrado (passou da data/hora do sorteio)
    isTicketClosed(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      
      // Se já está cancelado ou premiado, não está encerrado
      if (isCanceled || totalAwarded > 0) {
        return false;
      }
      
      // Verificar se passou da data do sorteio
      // O campo correto no DTO é 'dateTime' (com 'd' minúsculo)
      let drawDate = ticket.dateTime || ticket.betDateTime;
      
      // Se não há data do sorteio, não pode estar encerrado
      if (!drawDate) return false;
      
      // Para bilhetes com múltiplas loterias, considerar a data mais recente
      // Isso assume que todas as loterias do bilhete são para o mesmo sorteio
      const drawDateTime = new Date(drawDate);
      const now = new Date();
      
      // Considerar encerrado se passou da data do sorteio
      return now > drawDateTime;
    },

    showCancelButton(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      
      // Mostra o botão se não estiver cancelado, não tiver prêmios e não estiver encerrado
      return !isCanceled && totalAwarded === 0 && !isClosed;
    },

    canCancelTicket(ticket) {
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isClosed = this.isTicketClosed(ticket);
      
      // Só pode cancelar se:
      // 1. Não estiver cancelado
      // 2. Não tiver prêmios (totalAwarded = 0)
      // 3. Não estiver encerrado (sorteio ainda não ocorreu)
      if (isCanceled || totalAwarded > 0 || isClosed) {
        return false;
      }

      // BANCA e ADMIN podem cancelar a qualquer momento até o horário do sorteio (sem prazo de X minutos)
      if (this.isBanca || this.isAdmin) {
        return true;
      }

      // Demais cargos: verificar se ainda está dentro do prazo de cancelamento (ex.: 5 min após a aposta)
      const deadline = this.getCancelDeadline(ticket);
      if (!deadline) return false;

      const now = new Date();
      return now <= deadline;
    },

    getCancelButtonTitle(ticket) {
      if (!this.canCancelTicket(ticket)) {
        const timeRemaining = this.getTimeRemaining(ticket);
        if (timeRemaining === "Expirado") {
          return "Prazo para cancelamento expirado";
        }
        return `Tempo restante: ${timeRemaining}`;
      }
      return "Cancelar bilhete";
    },

    getCancelDeadline(ticket) {
      // Esta função calcula quando expira o prazo de cancelamento
      // O valor de MaxHourToCancelBet vem dos parâmetros da região via backend
      if (!ticket.dateTimePersisted || !ticket.maxHourToCancelBet) return null;
      
      const betDate = new Date(ticket.dateTimePersisted);
      // MaxHourToCancelBet agora vem em horas decimais (ex: 0.0833 para 5 minutos)
      const maxHours = ticket.maxHourToCancelBet;
      const deadline = new Date(betDate.getTime() + (maxHours * 60 * 60 * 1000));
      
      return deadline;
    },

    getTimeRemaining(ticket) {
      const deadline = this.getCancelDeadline(ticket);
      if (!deadline) return null;
      
      const now = new Date();
      const timeLeft = deadline - now;
      
      if (timeLeft <= 0) return "Expirado";
      
      const hours = Math.floor(timeLeft / (1000 * 60 * 60));
      const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
      
      if (hours > 0) {
        return `${hours}h ${minutes}m restantes`;
      } else {
        return `${minutes}m restantes`;
      }
    },

    confirmCancelTicket(ticket) {
      if (!this.canCancelTicket(ticket)) {
        toast.error('Este bilhete não pode ser cancelado.');
        return;
      }

      this.ticketToCancel = ticket;
      this.showCancelModal = true;
    },

    closeCancelModal() {
      this.showCancelModal = false;
      this.ticketToCancel = null;
      this.cancelLoading = false;
    },

    async processCancelTicket() {
      if (!this.ticketToCancel) return;

      if (!this.canCancelTicket(this.ticketToCancel)) {
        toast.error('Este bilhete não pode ser cancelado.');
        return;
      }

      this.cancelLoading = true;
      try {
        await mainService.cancelBet(
          this.ticketToCancel.betId, 
          this.currentUser.id, 
          'Cancelado pelo usuário'
        );
        toast.success('Bilhete cancelado com sucesso!');
        this.searchTickets(); // Recarrega os bilhetes após o cancelamento
        this.closeCancelModal();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.cancelLoading = false;
      }
    },

    getTotalAwardValue(awards) {
      if (!Array.isArray(awards)) return 0;
      return awards.reduce((sum, award) => sum + parseFloat(award.value || 0), 0);
    },

    getSellerNameById(sellerId) {
      if (!sellerId || !this.sellers || !Array.isArray(this.sellers)) {
        return 'Vendedor não identificado';
      }
      
      const seller = this.sellers.find(s => s.id === sellerId);
      if (seller) {
        // Mostrar nome e login se ambos existirem
        const name = seller.name || 'Nome não disponível';
        const login = seller.login || seller.Login;
        return login ? `${name} - ${login}` : name;
      }
      return 'Vendedor não encontrado';
    },

    getStatusWithCancelDate(ticketDetails) {
      if (!ticketDetails) return '';
      if (ticketDetails.status === 'Cancelado' && ticketDetails.canceledDateTime) {
        const dateStr = this.formatDateTimeForCancel(ticketDetails.canceledDateTime);
        return `Cancelado ${dateStr}`;
      }
      return ticketDetails.status || '';
    },

    formatDateTimeForCancel(dateTimeString) {
      try {
        if (!dateTimeString) return '';
        const str = String(dateTimeString);
        if (str.includes('T')) {
          const [datePart, timePart] = str.split('T');
          const [y, m, d] = datePart.split('-');
          const time = (timePart || '').split(':').slice(0, 2).join(':').replace(/\.\d+/, '').replace(/Z$/i, '');
          return `${d}/${m}/${y} ${time}`;
        }
        return str;
      } catch {
        return String(dateTimeString);
      }
    },

    isPrizeClaimedInDetails(ticketDetails) {
      return ticketDetails && (ticketDetails.prizeClaimed === true || ticketDetails.prizeClaimed === 'true');
    },

    getTotalPrizeValue(ticketDetails) {
      if (!ticketDetails || !ticketDetails.lotteries || !Array.isArray(ticketDetails.lotteries)) {
        return 0;
      }
      
      let totalPrize = 0;
      ticketDetails.lotteries.forEach(lottery => {
        if (lottery.numbers && Array.isArray(lottery.numbers)) {
          lottery.numbers.forEach(number => {
            if (number.numberAwards && Array.isArray(number.numberAwards)) {
              totalPrize += this.getTotalAwardValue(number.numberAwards);
            }
          });
        }
      });
      
      return totalPrize;
    },

    getCurrentDate() {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    },

    // Obter o número de dias para expiração do prêmio da região atual
    getPrizeExpirationDays() {
      if (this.currentRegionData && this.currentRegionData.prizeExpirationDays) {
        return this.currentRegionData.prizeExpirationDays;
      }
      return 7; // Valor padrão se não houver configuração
    },

    // Verificar se o prêmio expirou (baseado nos dias configurados na região)
    isPrizeExpired(ticket) {
      // Se não tem prêmio ou já foi resgatado, não está expirado
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === 'true';

      if (totalAwarded <= 0 || isPrizeClaimed) {
        return false;
      }

      // Verificar se passou X dias da data do sorteio (baseado na configuração da região)
      // O campo correto no DTO é 'dateTime' (com 'd' minúsculo)
      const betDateTime = ticket.dateTime || ticket.betDateTime;
      if (!betDateTime) {
        return false;
      }

      const drawDate = new Date(betDateTime);
      drawDate.setHours(0, 0, 0, 0); // Zerar horas para comparar apenas datas

      // Usar os dias de expiração que vêm do backend (do bilhete)
      const expirationDays = ticket.prizeExpirationDays || this.getPrizeExpirationDays();

      // Adicionar X dias à data do sorteio
      const expiryDate = new Date(drawDate);
      expiryDate.setDate(expiryDate.getDate() + expirationDays);

      const now = new Date();
      now.setHours(0, 0, 0, 0); // Zerar horas para comparar apenas datas

      return now > expiryDate;
    },

    // Agrupar números por betGroupOrder (bilhete principal e mais valendo)
    groupNumbersByBetGroup(numbers) {
      if (!numbers || !Array.isArray(numbers)) return [];

      const groups = {};
      numbers.forEach(number => {
        const groupOrder = number.betGroupOrder || 0;
        if (!groups[groupOrder]) {
          groups[groupOrder] = [];
        }
        groups[groupOrder].push(number);
      });

      // Converter objeto em array ordenado por betGroupOrder
      // O primeiro grupo (menor betGroupOrder) é o JOGO PRINCIPAL
      // Os demais são + VALENDO
      return Object.keys(groups)
        .sort((a, b) => parseInt(a) - parseInt(b))
        .map(groupOrder => ({
          groupOrder: parseInt(groupOrder),
          numbers: groups[groupOrder]
        }));
    },

    /** Números combinados (Binguinho, Lotinha, Duque/Terno…): "01|07|12" */
    isCombinedNumber(value) {
      return typeof value === 'string' && value.includes('|');
    },

    splitCombinedNumber(value) {
      if (!this.isCombinedNumber(value)) return [];
      return value
        .split('|')
        .map(part => {
          const trimmed = (part || '').trim();
          if (!trimmed) return '';
          const n = parseInt(trimmed, 10);
          if (Number.isNaN(n)) return trimmed;
          return String(n).padStart(2, '0');
        })
        .filter(Boolean);
    },

    formatModalityLabel(numberRow) {
      const name = numberRow?.modalityDescription || '';
      if (!this.isCombinedNumber(numberRow?.number)) return name;
      const upper = name.toUpperCase();
      const isPoolStyle =
        upper.includes('BINGUINHO') ||
        upper.includes('LOTINHA') ||
        upper.includes('QUININHA') ||
        upper.includes('SENINHA');
      if (!isPoolStyle) return name;
      const count = this.splitCombinedNumber(numberRow.number).length;
      return count > 0 ? `${name} (${count} dezenas)` : name;
    },

    // Agrupar números por modalidade
    groupNumbersByModality(numbers) {
      if (!numbers || !Array.isArray(numbers)) return {};

      const groups = {};
      numbers.forEach(number => {
        const key = `${number.modalityDescription}_${number.placing}`;
        if (!groups[key]) {
          groups[key] = [];
        }
        groups[key].push(number);
      });

      return groups;
    },

    // Agrupar números por modalidade dentro de um grupo e retornar como array ordenado
    // Ordena por plusValueType: 0 = JOGO PRINCIPAL, 1+ = + VALENDO
    getModalitiesInGroup(numbers) {
      if (!numbers || !Array.isArray(numbers)) return [];

      // Agrupar por modalidade (modalityDescription + placing)
      const modalityGroups = {};
      
      numbers.forEach(number => {
        // Agrupar apenas por modalityDescription e placing
        const key = `${number.modalityDescription}_${number.placing}`;
        if (!modalityGroups[key]) {
          modalityGroups[key] = [];
        }
        modalityGroups[key].push(number);
      });

      // Converter para array e ordenar por plusValueType primeiro (0 = principal, depois 1, 2, 3...)
      // Depois ordenar por nome da modalidade para manter consistência
      return Object.entries(modalityGroups)
        .sort((a, b) => {
          // Pegar o plusValueType do primeiro número de cada grupo
          const plusValueTypeA = a[1][0]?.plusValueType || 0;
          const plusValueTypeB = b[1][0]?.plusValueType || 0;
          
          // Primeiro ordenar por plusValueType (0 primeiro, depois 1, 2, 3...)
          if (plusValueTypeA !== plusValueTypeB) {
            return plusValueTypeA - plusValueTypeB;
          }
          
          // Se plusValueType for igual, ordenar por nome da modalidade
          const nameA = a[1][0]?.modalityDescription || '';
          const nameB = b[1][0]?.modalityDescription || '';
          return nameA.localeCompare(nameB);
        })
        .map(entry => entry[1]);
    },

    // Verificar se deve mostrar botão de resgate
    showRedeemButton(ticket) {
      const totalAwarded = parseFloat(ticket.totalAwarded || 0);
      const isPrizeClaimed = ticket.prizeClaimed === true || ticket.prizeClaimed === 'true';
      const isCanceled = ticket.canceled === true || ticket.canceled === 'true';
      const isExpired = this.isPrizeExpired(ticket);

      // Mostrar botão se:
      // 1. Tem prêmio (totalAwarded > 0)
      // 2. Não foi resgatado ainda
      // 3. Não está cancelado
      // 4. Está dentro do prazo de validade
      return totalAwarded > 0 && !isPrizeClaimed && !isCanceled && !isExpired;
    },

    // Abrir modal de resgate
    openRedeemModal(ticket) {
      if (!this.showRedeemButton(ticket)) {
        toast.error('Este prêmio não pode ser resgatado.');
        return;
      }

      this.ticketToRedeem = ticket;
      this.redeemPin = '';
      this.showRedeemModal = true;
    },

    // Fechar modal de resgate
    closeRedeemModal() {
      this.showRedeemModal = false;
      this.ticketToRedeem = null;
      this.redeemPin = '';
      this.redeemLoading = false;
    },

    // Processar resgate de prêmio
    async processRedeemPrize() {
      if (!this.ticketToRedeem) return;

      if (!this.redeemPin || this.redeemPin.trim() === '') {
        toast.error('Digite o NSU ou PIN do bilhete.');
        return;
      }

      // Validar se o PIN/NSU informado corresponde ao bilhete
      const pinInput = this.redeemPin.trim();
      const isValidPin = pinInput === this.ticketToRedeem.nsu || 
                         pinInput === this.ticketToRedeem.qrCode;

      if (!isValidPin) {
        toast.error('NSU ou PIN inválido. Verifique e tente novamente.');
        return;
      }

      this.redeemLoading = true;
      try {
        // Usar endpoint específico da BANCA que aceita NSU ou PIN
        // O APP do vendedor usa endpoint diferente que aceita apenas PIN
        await mainService.claimPrizeByBanca(
          this.ticketToRedeem.betId,
          this.currentUser.id,
          pinInput
        );

        toast.success('Prêmio resgatado com sucesso!');
        this.closeRedeemModal();
        
        // Recarregar os bilhetes para atualizar o status
        await this.searchTickets();
      } catch (error) {
        toast.error(handleErrorMessage(error));
      } finally {
        this.redeemLoading = false;
      }
    }
  }
};
</script>

<style scoped>
/* Estilos base aproveitando o tema existente */

/* Informação do vendedor selecionado */
.seller-filter-info {
  font-size: 0.875rem;
  color: var(--accent-primary);
  font-weight: 500;
  margin-left: 0.5rem;
}

/* Cartão de resumo */
.summary-card {
  background: var(--bg-secondary);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid var(--card-border);
  box-shadow: var(--card-shadow);
}

.summary-content {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--card-border);
}

.summary-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--accent-primary);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.summary-info {
  display: flex;
  flex-direction: column;
}

.summary-label {
  font-size: 0.875rem;
  color: var(--text-secondary);
  margin-bottom: 0.25rem;
}

.summary-value {
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--text-primary);
}

/* Informações específicas dos bilhetes */
.table-count {
  font-size: 0.875rem;
  color: var(--text-secondary);
  font-weight: 400;
  margin-left: 0.5rem;
}

.nsu-info {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.nsu-code {
  font-family: monospace;
  font-weight: 500;
  color: var(--text-primary);
}

.package-badge {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 700;
  background: #0d6efd;
  color: #fff;
  line-height: 1.4;
}

.qr-code {
  color: var(--accent-primary);
}

.datetime-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date {
  font-weight: 500;
  color: var(--text-primary);
}

.time {
  font-size: 0.875rem;
  color: var(--text-secondary);
}

/* Status badges */
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.875rem;
  font-weight: 500;
}

.status-active {
  background: #d4edda;
  color: #155724;
}

.status-canceled {
  background: #f8d7da;
  color: #721c24;
}

.status-awarded {
  background: #fff3cd;
  color: #856404;
}

.status-expired {
  background: #f0ad4e;
  color: #ffffff;
}

.status-closed {
  background: #e2e3e5;
  color: #495057;
}

.currency-positive {
  color: #28a745;
  font-weight: 600;
}

/* Ações */
.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-action {
  padding: 0.5rem;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-view {
  background: var(--accent-primary);
  color: white;
}

.btn-view:hover {
  background: var(--accent-primary-dark);
}

.btn-qr {
  background: #17a2b8;
  color: white;
}

.btn-qr:hover {
  background: #138496;
}

.btn-redeem {
  background: #28a745;
  color: white;
}

.btn-redeem:hover {
  background: #218838;
}

.btn-cancel {
  background: #dc3545;
  color: white;
  padding: 0.875rem 1.5rem;
  font-size: 0.95rem;
  font-weight: 500;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
  cursor: pointer;
  text-decoration: none;
}

.btn-cancel:hover:not(:disabled) {
  background: #c82333;
}

.btn-cancel.btn-disabled,
.btn-cancel:disabled {
  background: #6c757d;
  color: #adb5bd;
  cursor: not-allowed;
  opacity: 0.7;
}

.btn-cancel.btn-disabled:hover,
.btn-cancel:disabled:hover {
  background: #6c757d;
  color: #adb5bd;
}

/* Paginação */
.pagination-info {
  display: flex;
  align-items: center;
  gap: 1rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
}

.pagination-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.btn-pagination {
  padding: 0.5rem;
  border: 1px solid var(--card-border);
  background: var(--bg-primary);
  color: var(--text-primary);
  border-radius: 6px;
  cursor: pointer;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.btn-pagination:hover:not(:disabled) {
  background: var(--accent-primary);
  color: white;
  border-color: var(--accent-primary);
}

.btn-pagination:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-info {
  font-weight: 500;
  color: var(--text-primary);
  min-width: 60px;
  text-align: center;
}

.pagination-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-top: 1px solid var(--card-border);
  background: var(--bg-primary);
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

/* Modais */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: var(--bg-secondary);
  border-radius: 12px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid var(--card-border);
}

.modal-content.pin-modal {
  max-width: 500px;
}

.modal-content.cancel-modal {
  max-width: 500px;
}

.modal-content.redeem-modal {
  max-width: 550px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid var(--card-border);
}

.modal-header h3 {
  margin: 0;
  color: var(--text-primary);
}

.btn-close {
  background: none;
  border: none;
  font-size: 1.25rem;
  cursor: pointer;
  color: var(--text-secondary);
  padding: 0.5rem;
  border-radius: 6px;
  transition: var(--transition);
}

.btn-close:hover {
  background: var(--hover-color);
  color: var(--text-primary);
}

.modal-body {
  padding: 1.5rem;
}

/* Detalhes do bilhete */
.ticket-details {
  display: grid;
  gap: 1rem;
}

.detail-group {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: var(--bg-primary);
  border-radius: 8px;
}



.detail-group label {
  font-weight: 500;
  color: var(--text-secondary);
}

.detail-group label i {
  margin-right: 0.5rem;
  color: var(--accent-primary);
}

.detail-group span {
  font-weight: 500;
  color: var(--text-primary);
}

.draw-date-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.lottery-draw-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 1rem;
  background: var(--bg-secondary);
  border-radius: 6px;
  border: 1px solid var(--card-border);
  min-height: 44px;
}

.lottery-name {
  font-weight: 500;
  color: var(--accent-primary);
  font-size: 0.95rem;
  flex: 1;
}

.draw-date {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
  margin-left: 1rem;
  white-space: nowrap;
}

.lottery-item {
  margin-bottom: 1.5rem;
  padding: 1.25rem;
  background: var(--bg-primary);
  border-radius: 12px;
  border: 1px solid var(--card-border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.lottery-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.25rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid var(--card-border);
}

.lottery-title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.lottery-title i {
  color: var(--accent-primary);
  font-size: 1.25rem;
}

.lottery-title h5 {
  margin: 0;
  color: var(--text-primary);
  font-size: 1.1rem;
  font-weight: 600;
}

.lottery-draw-date {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
}

.lottery-draw-date i {
  color: var(--accent-primary);
}

/* Grupos de apostas (Principal e Mais Valendo) */
.bet-group {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: var(--bg-secondary);
  border-radius: 10px;
  border: 2px solid var(--card-border);
}

.bet-group:last-child {
  margin-bottom: 0;
}

.bet-group-header {
  margin-bottom: 1rem;
}

.bet-group-title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  font-size: 1rem;
  color: var(--accent-primary);
  padding: 0.5rem 0.75rem;
  background: var(--bg-primary);
  border-radius: 6px;
  border-left: 4px solid var(--accent-primary);
}

.bet-group-title i {
  font-size: 1.1rem;
}

/* Grupos de modalidades */
.modality-group {
  margin-bottom: 1rem;
  padding: 1rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--card-border);
}

.modality-group:last-child {
  margin-bottom: 0;
}

.modality-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid var(--card-border);
}

.modality-name-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modality-name {
  font-weight: 600;
  color: var(--text-primary);
  font-size: 0.95rem;
}

.modality-placing {
  font-size: 0.875rem;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-weight: 500;
}

.valendo-badge {
  font-size: 0.75rem;
  color: #ffffff;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-hover)) !important;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-weight: 600;
}

/* Container dos números */
.numbers-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

/* Badge do número */
.number-badge {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-width: 80px;
  padding: 0.75rem;
  background: var(--bg-secondary);
  border: 2px solid var(--card-border);
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: default;
}

.number-badge.number-badge-combo {
  min-width: min(100%, 320px);
  max-width: 100%;
  align-items: stretch;
  gap: 0.5rem;
}

.combo-dezenas {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  justify-content: flex-start;
}

.dezena-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.35rem;
  border-radius: 999px;
  background: var(--primary-color, #0288d1);
  color: #fff;
  font-size: 0.85rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.number-badge.number-awarded .dezena-chip {
  background: #e6a800;
  color: #1a1a1a;
}

.number-meta {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.15rem;
}

.combo-count {
  font-size: 0.75rem;
  color: var(--text-secondary, #6b7280);
  font-weight: 600;
}

.number-badge:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.number-badge.number-awarded {
  background: linear-gradient(135deg, #fff3cd 0%, #ffeaa7 100%);
  border-color: #ffc107;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
}

.number-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

.number-badge.number-awarded .number-value {
  color: #856404;
}

.number-amount {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--accent-primary);
  margin-bottom: 0.25rem;
}

.number-award-indicator {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  margin-top: 0.5rem;
  padding: 0.25rem 0.5rem;
  background: #28a745;
  color: white;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 600;
}

.number-award-indicator i {
  font-size: 0.75rem;
}

.lotteries-section {
  margin-top: 1.5rem;
}

.lotteries-section h4 {
  margin-bottom: 1rem;
  color: var(--text-primary);
}

/* Modal de PIN */
.pin-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem;
}

.pin-code-info {
  text-align: center;
  width: 100%;
  max-width: 500px;
}

.pin-code-label {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  font-size: 1.2rem;
}

.pin-code-label i {
  color: var(--accent-primary);
  font-size: 1.5rem;
}

.pin-code-display {
  font-family: 'Courier New', monospace;
  font-size: 2rem;
  font-weight: 700;
  color: var(--accent-primary);
  background: var(--bg-secondary);
  padding: 1.5rem 2rem;
  border-radius: 12px;
  word-break: break-all;
  margin-bottom: 1.5rem;
  border: 3px solid var(--accent-primary);
  letter-spacing: 0.1em;
  box-shadow: 0 4px 12px rgba(52, 120, 242, 0.2);
}

[data-theme="dark"] .pin-code-display {
  background: rgba(52, 120, 242, 0.1);
  color: #6ea8fe;
  border-color: #6ea8fe;
}

.pin-code-instructions {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 0.75rem;
  font-size: 0.95rem;
  color: var(--text-secondary);
  line-height: 1.6;
  padding: 0 1rem;
  text-align: left;
}

.pin-code-instructions i {
  color: var(--accent-primary);
  font-size: 1.1rem;
  flex-shrink: 0;
  margin-top: 0.2rem;
}

/* Confirmação de Cancelamento */
.cancel-confirmation {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.5rem;
  background: var(--bg-primary);
  border-radius: 8px;
  border: 1px solid var(--card-border);
}

.warning-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--warning-text, #856404);
  background: var(--warning-bg, #fff3cd);
  padding: 0.75rem;
  border-radius: 6px;
  border: 1px solid var(--warning-border, #ffeeba);
}

.warning-message i {
  font-size: 1.25rem;
  color: var(--warning-text, #856404);
}

.warning-content h4 {
  margin: 0 0 0.5rem 0;
  color: var(--warning-text, #856404);
}

.warning-content p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--warning-text, #856404);
}

.ticket-info {
  background: var(--bg-secondary);
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid var(--card-border);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
}

.info-label {
  font-weight: 500;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.info-value {
  font-weight: 500;
  color: var(--text-primary);
  font-size: 0.9rem;
}

.time-remaining {
  color: #dc3545; /* Cor para tempo expirado */
  font-weight: 600;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
  padding: 1.5rem;
  border-top: 1px solid var(--card-border);
}

.modal-footer .btn-secondary {
  background: var(--bg-secondary);
  color: var(--text-primary);
  border: 1px solid var(--card-border);
}

.modal-footer .btn-secondary:hover:not(:disabled) {
  background: var(--hover-color);
  color: var(--text-primary);
  border-color: var(--hover-color);
}

.modal-footer .btn-danger {
  background: #dc3545;
  color: white;
}

.modal-footer .btn-danger:hover:not(:disabled) {
  background: #c82333;
}

.modal-footer .btn-danger:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Modal de Resgate de Prêmio */
.redeem-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 1rem;
}

.success-message {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #155724;
  background: #d4edda;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #c3e6cb;
}

.success-message i {
  font-size: 1.5rem;
  color: #28a745;
}

.success-content h4 {
  margin: 0 0 0.5rem 0;
  color: #155724;
  font-size: 1.1rem;
}

.success-content p {
  margin: 0;
  font-size: 0.9rem;
  color: #155724;
}

.input-group-redeem {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 1rem;
}

.input-group-redeem .input-label {
  font-weight: 600;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.95rem;
}

.input-group-redeem .input-label i {
  color: var(--accent-primary);
}

.input-group-redeem .form-input {
  padding: 0.75rem;
  border: 2px solid var(--card-border);
  border-radius: 8px;
  font-size: 1rem;
  font-family: monospace;
  transition: border-color 0.3s ease;
}

.input-group-redeem .form-input:focus {
  border-color: var(--accent-primary);
  outline: none;
  box-shadow: 0 0 0 3px rgba(52, 120, 242, 0.1);
}

.input-hint {
  font-size: 0.85rem;
  color: var(--text-secondary);
  font-style: italic;
}

.prize-value {
  color: #28a745 !important;
  font-weight: 700 !important;
  font-size: 1.1rem !important;
}

.validity-ok {
  color: #28a745 !important;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
}

.validity-ok i {
  font-size: 1.1rem;
}

.btn-success {
  background: #28a745;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.3s ease;
}

.btn-success:hover:not(:disabled) {
  background: #218838;
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(40, 167, 69, 0.3);
}

.btn-success:disabled {
  background: #6c757d;
  cursor: not-allowed;
  opacity: 0.6;
}

/* Correção para campos de data */
.form-input[type="date"] {
  color: var(--text-primary) !important;
  font-family: inherit;
}

.form-input[type="date"]::-webkit-datetime-edit {
  color: var(--text-primary) !important;
}

.form-input[type="date"]::-webkit-datetime-edit-text {
  color: var(--text-primary) !important;
}

.form-input[type="date"]::-webkit-datetime-edit-month-field {
  color: var(--text-primary) !important;
}

.form-input[type="date"]::-webkit-datetime-edit-day-field {
  color: var(--text-primary) !important;
}

.form-input[type="date"]::-webkit-datetime-edit-year-field {
  color: var(--text-primary) !important;
}

.form-input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.5);
  cursor: pointer;
}

[data-theme="dark"] .form-input[type="date"]::-webkit-calendar-picker-indicator {
  filter: invert(0.8);
}

/* Garantir que todos os campos de data tenham a mesma cor */
.form-input[type="date"]:disabled {
  opacity: 0.6;
}

/* Responsivo */
@media (max-width: 768px) {
  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .action-buttons {
    flex-direction: column;
    gap: 0.25rem;
  }

  .btn-action {
    width: 100%;
  }

  .summary-content {
    grid-template-columns: 1fr;
  }

  .pagination-footer {
    flex-direction: column;
    gap: 1rem;
  }

  .card-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  /* Responsivo para modal de detalhes */
  .lottery-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }

  .lottery-draw-date {
    width: 100%;
    justify-content: center;
  }

  .bet-group {
    padding: 0.75rem;
  }

  .modality-group {
    padding: 0.75rem;
  }

  .modality-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .numbers-container {
    gap: 0.5rem;
  }

  .number-badge {
    min-width: 70px;
    padding: 0.5rem;
  }

  .number-value {
    font-size: 1.25rem;
  }

  .number-amount {
    font-size: 0.75rem;
  }

  .number-award-indicator {
    font-size: 0.7rem;
    padding: 0.2rem 0.4rem;
  }
  
  /* Modal de PIN responsivo */
  .pin-code-container {
    padding: 1rem;
  }
  
  .pin-code-info {
    max-width: 100%;
  }
  
  .pin-code-display {
    font-size: 1.5rem;
    padding: 1rem 1.5rem;
    letter-spacing: 0.05em;
  }
  
  .pin-code-label {
    font-size: 1rem;
  }
  
  .pin-code-instructions {
    font-size: 0.85rem;
    padding: 0 0.5rem;
  }

  /* Confirmação de Cancelamento responsivo */
  .cancel-confirmation {
    padding: 1rem;
  }

  .warning-message {
    padding: 0.75rem;
  }

  .warning-message i {
    font-size: 1rem;
  }

  .warning-content h4 {
    font-size: 1rem;
  }

  .warning-content p {
    font-size: 0.8rem;
  }

  .ticket-info {
    padding: 0.75rem;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .info-label {
    width: 100%;
    text-align: left;
  }

  .info-value {
    font-weight: 400;
    color: var(--text-secondary);
  }

  .time-remaining {
    font-size: 0.8rem;
  }

  .modal-footer {
    flex-direction: column;
    gap: 0.75rem;
  }

  .modal-footer .btn-secondary,
  .modal-footer .btn-danger,
  .modal-footer .btn-success {
    width: 100%;
  }

  /* Modal de resgate responsivo */
  .redeem-content {
    padding: 0.5rem;
  }

  .success-message {
    flex-direction: column;
    text-align: center;
    padding: 0.75rem;
  }

  .success-message i {
    font-size: 1.25rem;
  }

  .success-content h4 {
    font-size: 1rem;
  }

  .success-content p {
    font-size: 0.85rem;
  }

  .input-group-redeem .form-input {
    font-size: 0.9rem;
  }
}

/* Mensagem de permissão */
.permission-info {
  padding: 7px;
  background-color: #3478f2;
  border: 1px solid #548fff;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: white;
  margin-bottom: 20px;
}

.info-card {
  display: flex;
  align-items: center;
  gap: 8px;
}

.info-content {
  font-size: 0.9em;
  line-height: 1.4;
}

/* Badges para status */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.badge-success {
  background-color: #dcfce7;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.badge-warning {
  background-color: #fef3c7;
  color: #92400e;
  border: 1px solid #fde68a;
}
</style> 